import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { compose } from '@xo-union/react-css-modules';
import Alert, { Snackbar } from '@xo-union/tk-component-alerts';
import { TopLevelContainer } from '@xo-union/tk-component-grid';
import { DisplayButton } from '@xo-union/tk-ui-links';
import ExportGuestListModal from '../components/export-guest-list/export-modal';
import GuestListView from '../components/guest-list/guest-list-view';
import GLMHeader from '../components/guest-list/header/glm-header/glm-header';
import MessageGuestModal from '../components/guest-list/modals/message-guest-modal';
import RsvpSettingsModal from '../components/guest-list/modals/rsvp-settings-modal';
import OverviewListColumns from '../components/guest-list/overview-list-columns';
import SingleEventListColumns from '../components/guest-list/single-event-list-columns';
import HouseholdForm from '../components/shared/edit-household';
import Loader from '../components/shared/loader';
import AnalyticsStore from '../models/analytics-store';
import EventCollection from '../models/gds/event-collection';
import GdsHouseholdCollection from '../models/gds/gds-household-collection';
import GdsUiState from '../models/gds/gds-ui-state';
import GuestListStore from '../models/guest-list-store';
import UserStore from '../models/user-store';
import insertGoogleScript from '../utils/add-google-api-script';
import { temp_isValidAddress } from '../utils/address-utils';
import { getQueryParams } from '../utils/url';
import style from './guest-list-page.scss';
import { withRouter } from 'react-router-dom';
import { GOOGLE_API_KEY } from '~/glm/utils/constants';

@withRouter
@inject(
  'gdsUiState',
  'gdsEvents',
  'initGuestListPromise',
  'userStore',
  'analyticsStore',
  'gdsHouseholds',
  'guestListStore'
)
@observer
class GuestListPage extends Component {
  static propTypes = {
    gdsUiState: PropTypes.instanceOf(GdsUiState),
    gdsEvents: PropTypes.instanceOf(EventCollection),
    gdsHouseholds: PropTypes.instanceOf(GdsHouseholdCollection),
    initGuestListPromise: PropTypes.instanceOf(Promise),
    userStore: PropTypes.instanceOf(UserStore),
    analyticsStore: PropTypes.instanceOf(AnalyticsStore),
    guestListStore: PropTypes.instanceOf(GuestListStore),
    location: PropTypes.any,
  };

  constructor(props, context) {
    super(props, context);
    const { analyticsStore, initGuestListPromise, gdsUiState } = props;
    this.state = {
      isReady: false,
      showMessage: false,
      open: false,
    };

    this.analyticsStore = analyticsStore;

    //create ref for snackbar and put into gdsUiState
    gdsUiState.setSnackbarRef(React.createRef());
    initGuestListPromise
      .then(this.setInitialEvent)
      .then(() => {
        this.setState({
          isReady: true,
          showMessage: true,
        });
      })
      .catch(err => {
        console.error(err);
      }); // eslint-disable-line no-console
  }

  closeModal = () => this.setState({ open: false });

  openAddEventModal = () => {
    const { analyticsStore } = this.props;
    this.setState({ open: true });
    analyticsStore.track('Dashboard Interaction', {
      action: 'add event initiated',
      source: 'guest list',
    });
  };

  handleSnackbarViewFormClick = formLink => {
    this.trackSnackbarViewFormClick(formLink);
    if (formLink) window.open(formLink, '_blank');
  };

  trackSnackbarViewFormClick = formLink => {
    return this.analyticsStore.track('Dashboard Interaction', {
      action: 'snackbar view form clicked',
      source: 'guest list',
      formLink,
    });
  };

  trackSnackbarOnClose = () => {
    return this.analyticsStore.track('Dashboard Interaction', {
      action: 'snackbar closed',
      source: 'guest list',
    });
  };

  trackEditHousehold = address => {
    return this.analyticsStore.track('Dashboard Interaction', {
      action: 'household update initiated',
      attemptAddressBackfill: temp_isValidAddress(address),
    });
  };

  editHousehold = household => {
    const { gdsUiState } = this.props;
    gdsUiState.setEditingHousehold(household);
    gdsUiState.setShowHouseholdForm(true);
    return this.trackEditHousehold(household.getAddress);
  };

  trackMeal = value => {
    return this.analyticsStore.track('Dashboard Interaction', {
      action: 'guest updated',
      meal: value,
    });
  };

  trackSelectAllGuests = () => {
    return this.analyticsStore.track('Dashboard Interaction', {
      action: 'select all guests',
    });
  };

  trackSelectAll = () => {
    return this.analyticsStore.track('Dashboard Interaction', {
      action: 'select all guests',
    });
  };

  trackInviteSend = value => {
    return this.analyticsStore.track('Dashboard Interaction', {
      invite: value,
    });
  };

  trackThankYou = value => {
    return this.analyticsStore.track('Dashboard Interaction', {
      thankYou: value,
    });
  };

  trackToggleAll = () => {
    return this.analyticsStore.track('Dashboard Interaction', {
      action: 'select household',
    });
  };

  trackFilter = () => {
    return this.analyticsStore.track('Dashboard Interaction', {
      action: 'Filter by RSVP',
    });
  };

  setInitialEvent = () => {
    const { gdsUiState, gdsEvents } = this.props;
    const query = getQueryParams(this.props.location.search);
    const { event, party } = query;

    //set to all events if no event is selected
    if (event) {
      gdsUiState.setSelectedEvent(gdsEvents.get(event));
    } else {
      gdsUiState.setAllGuestsView(party);
    }
  };

  componentDidMount() {
    // scroll to top of GLM page when navigating from CV2 dashboard
    window.scrollTo(0, 0);

    // eslint-disable-next-line no-undef
    insertGoogleScript(GOOGLE_API_KEY);
  }

  componentDidUpdate(prevProps) {
    const { prevEvent, prevFilter, prevParty } = getQueryParams(prevProps.location.search);
    const { event, filter, party } = getQueryParams(this.props.location.search);

    if (event !== prevEvent || filter !== prevFilter || party !== prevParty) {
      return false;
    }

    this.props.gdsUiState.initUrlParams({
      event,
      filter,
      household: party !== 'false',
    });
    return this.renderSuccessMessage();
  }

  renderSuccessMessage() {
    const { showMessage } = this.state;
    const { location } = this.props;
    const { state } = location;
    if (state && state.message && this.alert && showMessage) {
      this.setState({ showMessage: false }, () => {
        this.alert.show({
          type: 'info',
          content: state.message,
        });
        this.timeoutId = setTimeout(() => {
          this.alert.dismiss();
        }, 5000);
      });
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeoutId);
  }

  render() {
    const { userStore, gdsUiState, gdsEvents, gdsHouseholds, guestListStore, analyticsStore } = this.props;

    if (!this.state.isReady) {
      return <Loader />;
    }

    // This controls if we show the example guestlist table for zero state
    let showTable = true;

    if (gdsUiState.allGuestsView && !gdsHouseholds.size) {
      // If allGuestsView and gdsHouseholds is greater than 0
      showTable = false;
    } else if (gdsUiState.selectedEvent && !gdsUiState.selectedEvent.invitedHouseholds.length) {
      showTable = false;
    }
    return (
      <>
        <TopLevelContainer>
          <div className={style.selectedEventContainer}>
            <div className={style.mainContent}>
              <GLMHeader
                gdsUiState={gdsUiState}
                gdsEvents={gdsEvents}
                gdsHouseholds={gdsHouseholds}
                analyticsStore={analyticsStore}
                guestListStore={guestListStore}
                events={gdsEvents}
                userStore={userStore}
              />
              {showTable && (
                <div className={style.guestListView}>
                  <div className={style.rowContainer}>
                    <div ref={this.tableRef} className={style.rowContainerInner}>
                      <table className={style.guestTable}>
                        <thead>
                          {gdsUiState.selectedEvent ? (
                            <SingleEventListColumns
                              trackSelectAllGuests={this.trackSelectAllGuests}
                              rsvpEnabled={gdsEvents.rsvpEnabled}
                              numberOfHouseholds={gdsUiState.selectedEvent.invitedHouseholds.length}
                              guestListStore={guestListStore}
                              gdsUiState={gdsUiState}
                              hasWWS={userStore.hasWWS}
                              usesQuestions={guestListStore.guestWedding.usesQuestions}
                            />
                          ) : (
                            <OverviewListColumns
                              trackSelectAllGuests={this.trackSelectAllGuests}
                              gdsEvents={gdsEvents}
                              guestListStore={guestListStore}
                              numberOfHouseholds={gdsHouseholds.householdsCount}
                              gdsUiState={gdsUiState}
                            />
                          )}
                        </thead>
                        <tbody>
                          <GuestListView
                            events={gdsEvents}
                            gdsUiState={gdsUiState}
                            gdsHouseholds={gdsHouseholds}
                            gdsEvents={gdsEvents}
                            userStore={userStore}
                            usesQuestions={guestListStore.guestWedding.usesQuestions}
                            analyticsStore={analyticsStore}
                            hasWWS={userStore.hasWWS}
                            handleEditHousehold={this.editHousehold}
                            handleTrackFilter={this.trackFilter}
                            handleTrackInviteSend={this.trackInviteSend}
                            handleTrackMeal={this.trackMeal}
                            handleTrackSelectAll={this.trackSelectAll}
                            handleTrackThankYou={this.trackThankYou}
                          />
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <Snackbar
            onClose={this.trackSnackbarOnClose}
            ref={gdsUiState.snackbarRef}
            classes={compose({ container: style.snackBarContainer })}
          >
            {data => {
              if (data && data.type === 'COPY_FORM_LINK') {
                return (
                  <>
                    Link copied! Send {data.name} their own unique link to add or edit their info.{' '}
                    <DisplayButton
                      color="primary-alternative"
                      onClick={() => this.handleSnackbarViewFormClick(data.formLink)}
                    >
                      View form
                    </DisplayButton>
                  </>
                );
              } else if (data) {
                return <>{data}</>;
              }

              return null;
            }}
          </Snackbar>

          <div className={`${style.fixedAlert} ${style.relative}`}>
            <Alert
              ref={a => {
                this.alert = a;
              }}
              position="bottom"
            />
          </div>
          {gdsUiState.rsvpModalView ? <RsvpSettingsModal /> : null}
          {gdsUiState.exportGuestModalView ? <ExportGuestListModal /> : null}
          {gdsUiState.messageGuestModalView ? <MessageGuestModal /> : null}
        </TopLevelContainer>
        <HouseholdForm gdsUiState={gdsUiState} />
      </>
    );
  }
}

export default GuestListPage;
